import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { initializeSocket } from '../services/vpsApi';
import {
  initGA,
  trackDisplayLogin,
  trackContentPlay,
  trackTimerDisplay,
  trackCarouselPlay,
  trackSessionDuration,
  trackDisplayRefresh,
  trackPageView
} from '../utils/analytics';

const API_URL = process.env.REACT_APP_API_URL || 'https://multi-counter.bancheng.tech/api';

const DisplayPage = () => {
  const { displayId } = useParams();
  const [pageData, setPageData] = useState(null);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [requireLogin, setRequireLogin] = useState(false);
  const [password, setPassword] = useState('');
  const [loginError, setLoginError] = useState('');
  const [isLoggingIn, setIsLoggingIn] = useState(false);
  const [videoLoading, setVideoLoading] = useState(false); // Track video loading state
  const timerRef = useRef(null);
  const socketRef = useRef(null);
  const sessionStartTime = useRef(Date.now()); // Track session start time
  const lastContentIdRef = useRef(null); // Track last played content to avoid duplicate logging
  const videoStartTimeRef = useRef(null); // Track when video started playing
  const currentVideoUrlRef = useRef(null); // Track current video URL for duration logging
  const currentVideoIdRef = useRef(null); // Track current video ID
  const iframeRef = useRef(null); // Reference to the YouTube iframe
  const [videoKey, setVideoKey] = useState(0); // Force iframe reload when needed
  const videoEndHandledRef = useRef(false); // Track if video end has been handled (use ref to persist across effect runs)
  const lastCarouselDataRef = useRef(null); // Track last carousel data to detect changes
  const lastPlayerStateRef = useRef(null); // Track last player state across effect runs

  // Generate a unique session ID for this browser session
  const [sessionId] = useState(() => {
    const saved = sessionStorage.getItem(`display_session_${displayId}`);
    if (saved) return saved;
    const newId = `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
    sessionStorage.setItem(`display_session_${displayId}`, newId);
    return newId;
  });

  // Login to display
  const handleLogin = async (forceLogin = false) => {
    try {
      setIsLoggingIn(true);
      setLoginError('');

      const endpoint = forceLogin ? 'force-login' : 'login';
      const response = await axios.post(`${API_URL}/display-page/${displayId}/${endpoint}`, {
        password,
        sessionId
      });

      if (response.data.success) {
        setRequireLogin(false);
        // Track login success
        trackDisplayLogin(displayId, response.data.display?.名稱 || displayId);
        await fetchData();
      } else if (response.data.needKickout) {
        const confirmed = window.confirm(
          '此顯示器已在其他地方登入。是否要強制登入？（將會登出其他會話）'
        );
        if (confirmed) {
          await handleLogin(true);
        }
      }
    } catch (err) {
      if (err.response?.data?.requirePassword) {
        setLoginError(err.response.data.error);
      } else {
        setLoginError(err.response?.data?.error || '登入失敗');
      }
    } finally {
      setIsLoggingIn(false);
    }
  };

  // Log content play event
  const logContentPlay = async (contentType, contentData, duration = null) => {
    try {
      await axios.post(`${API_URL}/display-page/${displayId}/log-play`, {
        sessionId,
        contentType, // 'timer', 'carousel', 'video', 'image'
        contentData, // { timerId, carouselId, itemId, etc }
        duration, // playback duration in seconds (for video)
        timestamp: new Date().toISOString()
      });

      // Track in Google Analytics
      trackContentPlay(displayId, contentType, contentData);
    } catch (err) {
      console.error('Failed to log content play:', err);
      // Don't throw - logging shouldn't break the display
    }
  };

  // Log video playback duration when video ends or changes
  const logVideoDuration = useCallback(async (videoUrl) => {
    if (videoStartTimeRef.current && videoUrl) {
      const duration = Math.floor((Date.now() - videoStartTimeRef.current) / 1000);
      if (duration > 0) {
        try {
          await axios.post(`${API_URL}/display-page/${displayId}/log-play`, {
            sessionId,
            contentType: 'video',
            contentData: {
              carouselId: pageData?.carouselId,
              carouselName: pageData?.carouselName,
              itemUrl: videoUrl,
              playbackComplete: true
            },
            duration,
            timestamp: new Date().toISOString()
          });
          console.log(`Video duration logged: ${duration} seconds for ${videoUrl}`);
        } catch (err) {
          console.error('Failed to log video duration:', err);
        }
      }
    }
    videoStartTimeRef.current = null;
  }, [displayId, sessionId, pageData?.carouselId, pageData?.carouselName]);

  // Send heartbeat with session duration
  const sendHeartbeat = async () => {
    try {
      const sessionDuration = Math.floor((Date.now() - sessionStartTime.current) / 1000); // in seconds
      await axios.post(`${API_URL}/display-page/${displayId}/heartbeat`, {
        sessionId,
        sessionDuration,
        timestamp: new Date().toISOString()
      });

      // Track session duration in Google Analytics (every 30 seconds)
      trackSessionDuration(displayId, sessionDuration);
    } catch (err) {
      console.error('Failed to send heartbeat:', err);
    }
  };

  // Fetch display page data
  const fetchData = async () => {
    try {
      const response = await axios.get(`${API_URL}/display-page/${displayId}`, {
        params: { sessionId }
      });
      setPageData(response.data);
      setLoading(false);
      setRequireLogin(false);
    } catch (err) {
      if (err.response?.status === 401 && err.response?.data?.requireLogin) {
        setRequireLogin(true);
        setLoading(false);
      } else {
        setError(err.response?.data?.error || '載入失敗');
        setLoading(false);
      }
    }
  };

  // Send heartbeat every 30 seconds
  useEffect(() => {
    // Send initial heartbeat
    sendHeartbeat();

    // Setup interval for regular heartbeats
    const heartbeatInterval = setInterval(() => {
      sendHeartbeat();
    }, 30000); // Every 30 seconds

    return () => clearInterval(heartbeatInterval);
  }, [displayId]);

  // Initialize WebSocket and subscribe to changes
  useEffect(() => {
    // Initialize Google Analytics on component mount
    initGA();

    // Track initial page view
    trackPageView(`/display/${displayId}`, `Display ${displayId}`);

    fetchData();

    // Setup WebSocket
    socketRef.current = initializeSocket();

    // Listen for refresh events - use fetchData instead of reload to prevent flashing
    const handleRefresh = (data) => {
      console.log('🔄 Display refresh triggered:', data);
      console.log('🔄 Refreshing display data (no reload)');

      // Track display refresh in Google Analytics
      trackDisplayRefresh(displayId, data.reason || 'unknown');

      fetchData(); // Use fetchData instead of window.location.reload() to prevent flashing
    };

    // Listen for timer force refresh events
    const handleTimerForceRefresh = (data) => {
      console.log('🔄 Timer force refresh event received:', data);
      // Check if this display is showing the timer that was refreshed
      if (pageData?.timer && pageData.timer._id === data.timerId) {
        console.log('🔄 Refreshing display data due to timer force refresh (no reload)');
        fetchData(); // Use fetchData instead of window.location.reload() to prevent flashing
      }
    };

    socketRef.current.on('display:refresh', handleRefresh);
    socketRef.current.on('timer:force-refresh', handleTimerForceRefresh);
    console.log('✅ Display refresh listeners attached');

    // Wait for socket to connect before subscribing
    const subscribeWhenReady = () => {
      if (socketRef.current.connected) {
        console.log('📺 Subscribing to display:', displayId);
        socketRef.current.emit('subscribe', { collection: 'display', documentId: displayId });
      } else {
        console.log('⏳ Waiting for WebSocket connection...');
        socketRef.current.once('connect', () => {
          console.log('📺 Subscribing to display:', displayId);
          socketRef.current.emit('subscribe', { collection: 'display', documentId: displayId });
        });
      }
    };

    subscribeWhenReady();

    // Also subscribe to company changes if we have the company ID
    if (pageData?.display?.公司ID) {
      socketRef.current.emit('subscribe', { collection: 'company', documentId: pageData.display.公司ID });
    }

    return () => {
      if (socketRef.current) {
        socketRef.current.off('display:refresh', handleRefresh);
        socketRef.current.off('timer:force-refresh', handleTimerForceRefresh);
        socketRef.current.emit('unsubscribe', { collection: 'display', documentId: displayId });
        if (pageData?.display?.公司ID) {
          socketRef.current.emit('unsubscribe', { collection: 'company', documentId: pageData.display.公司ID });
        }
      }
    };
  }, [displayId]);

  // Subscribe to company changes when pageData updates
  useEffect(() => {
    if (socketRef.current && pageData?.display?.公司ID) {
      socketRef.current.emit('subscribe', { collection: 'company', documentId: pageData.display.公司ID });

      return () => {
        if (socketRef.current && pageData?.display?.公司ID) {
          socketRef.current.emit('unsubscribe', { collection: 'company', documentId: pageData.display.公司ID });
        }
      };
    }
  }, [pageData?.display?.公司ID]);

  // Reset currentIndex when carousel data changes (for dynamic updates)
  useEffect(() => {
    if (pageData?.type === 'carousel' && pageData.data) {
      const newDataKey = JSON.stringify(pageData.data.map(item => ({
        圖片: item.圖片,
        影片: item.影片,
        圖片秒數: item.圖片秒數
      })));

      // If carousel data has changed, reset to the first item
      if (lastCarouselDataRef.current !== null && lastCarouselDataRef.current !== newDataKey) {
        console.log('🔄 Carousel data changed, resetting to first item');
        setCurrentIndex(0);
        setVideoKey(0);
        videoEndHandledRef.current = false;
        currentVideoIdRef.current = null;
        currentVideoUrlRef.current = null;
        videoStartTimeRef.current = null;
      }
      lastCarouselDataRef.current = newDataKey;
    }
  }, [pageData]);

  // Handle carousel auto-play
  useEffect(() => {
    if (pageData?.type === 'carousel' && pageData.data.length > 0) {
      const currentItem = pageData.data[currentIndex];

      // Log content play when carousel item changes
      const contentId = `${pageData.carouselId || 'carousel'}_${currentIndex}`;
      if (lastContentIdRef.current !== contentId) {
        lastContentIdRef.current = contentId;

        const contentType = currentItem.影片 ? 'video' : 'image';
        logContentPlay(contentType, {
          carouselId: pageData.carouselId,
          carouselName: pageData.carouselName,
          itemIndex: currentIndex,
          itemType: contentType,
          itemUrl: currentItem.影片 || currentItem.圖片
        });
      }

      // Clear any existing timer
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }

      // If it's an image, set timer for next slide
      if (currentItem.圖片 && !currentItem.影片) {
        const duration = (currentItem.圖片秒數 || 5) * 1000;
        console.log(`🖼️ Image display: setting ${duration}ms timer for index ${currentIndex}`);
        timerRef.current = setTimeout(() => {
          console.log(`⏰ Image timer fired, moving to next slide`);
          setCurrentIndex((prev) => (prev + 1) % pageData.data.length);
        }, duration);
      } else if (currentItem.影片) {
        console.log(`🎥 Video display: waiting for video end event for index ${currentIndex}`);
      }
    }

    return () => {
      if (timerRef.current) {
        clearTimeout(timerRef.current);
      }
    };
  }, [pageData, currentIndex]);

  // Extract YouTube video ID (supports regular videos, shorts, embeds)
  const getYouTubeId = (url) => {
    if (!url) return null;
    // Support: youtu.be/, youtube.com/v/, youtube.com/embed/, youtube.com/shorts/, youtube.com/watch?v=
    const regExp = /^.*(youtu\.be\/|youtube\.com\/(?:v\/|u\/\w\/|embed\/|shorts\/|watch\?v=|watch\?.+&v=))([^#&?\s]{11}).*/;
    const match = url.match(regExp);
    return (match && match[2]) ? match[2] : null;
  };

  // Handle video tracking and postMessage for video end detection
  useEffect(() => {
    if (pageData?.type === 'carousel' && pageData.data.length > 0) {
      const currentItem = pageData.data[currentIndex];
      const videoId = currentItem?.影片 ? getYouTubeId(currentItem.影片) : null;
      const videoUrl = currentItem?.影片;
      const dataLength = pageData.data.length;

      // Log duration of previous video if any (when slide changes before video ends)
      if (currentVideoUrlRef.current && videoStartTimeRef.current && currentVideoUrlRef.current !== videoUrl) {
        logVideoDuration(currentVideoUrlRef.current);
      }

      // Update tracking refs
      if (videoId) {
        if (currentVideoIdRef.current !== videoId) {
          currentVideoUrlRef.current = videoUrl;
          currentVideoIdRef.current = videoId;
          videoStartTimeRef.current = Date.now();
          setVideoLoading(true);
          // Reset video end handled flag for new video
          videoEndHandledRef.current = false;
          console.log(`Started tracking video: ${videoId}, reset videoEndHandled`);
        }
      } else {
        currentVideoUrlRef.current = null;
        currentVideoIdRef.current = null;
        videoStartTimeRef.current = null;
        setVideoLoading(false);
        // Reset video end handled flag when switching to non-video
        videoEndHandledRef.current = false;
      }

      // Reset player state when video changes
      if (currentVideoIdRef.current !== videoId) {
        lastPlayerStateRef.current = null;
      }

      // Function to handle video end - use refs to persist state across effect runs
      const handleVideoEnd = () => {
        if (videoEndHandledRef.current) {
          console.log('🔴 Video end already handled, skipping');
          return;
        }
        videoEndHandledRef.current = true;

        console.log(`🎬 Video ended. Current index: ${currentIndex}, Total items: ${dataLength}`);
        const nextIndex = (currentIndex + 1) % dataLength;
        const nextItem = pageData.data[nextIndex];
        const nextItemType = nextItem?.影片 ? 'video' : (nextItem?.圖片 ? 'image' : 'unknown');
        console.log(`➡️ Moving to next slide: ${nextIndex}, next item type: ${nextItemType}`);

        logVideoDuration(videoUrl);

        // If looping back to the same index (single item or same video), force reload
        if (nextIndex === currentIndex) {
          console.log('🔄 Same index, forcing video reload');
          setVideoKey(prev => prev + 1);
        } else {
          console.log(`✅ Setting currentIndex from ${currentIndex} to ${nextIndex}`);
          setCurrentIndex(nextIndex);
        }
      };

      // Listen for YouTube postMessage events (video state changes)
      const handleMessage = (event) => {
        // Only process messages from YouTube
        if (event.origin !== 'https://www.youtube.com') return;

        try {
          const data = JSON.parse(event.data);

          // Check for video end event (playerState 0 = ended)
          if (data.event === 'onStateChange' && data.info === 0) {
            console.log('Video ended via onStateChange');
            handleVideoEnd();
            return;
          }
          // Video started playing
          if (data.event === 'onStateChange' && data.info === 1) {
            console.log('Video playing via onStateChange');
            setVideoLoading(false);
            return;
          }

          // Handle infoDelivery event which contains video data
          if (data.event === 'infoDelivery' && data.info && data.info.playerState !== undefined) {
            const playerState = data.info.playerState;

            // Only process if state actually changed (use ref to persist across effect runs)
            if (playerState !== lastPlayerStateRef.current) {
              lastPlayerStateRef.current = playerState;
              console.log('🎮 Player state changed to:', playerState, '(-1=unstarted, 0=ended, 1=playing, 2=paused, 3=buffering)');

              // playerState: -1 (unstarted), 0 (ended), 1 (playing), 2 (paused), 3 (buffering), 5 (cued)
              if (playerState === 0) {
                console.log('🎬 Video ended via infoDelivery');
                handleVideoEnd();
              } else if (playerState === 1) {
                console.log('▶️ Video playing via infoDelivery');
                setVideoLoading(false);
              }
            }
          }
        } catch (e) {
          // Not a JSON message, ignore
        }
      };

      window.addEventListener('message', handleMessage);

      // Send listening command to YouTube iframe after a delay
      const sendListeningCommand = () => {
        if (iframeRef.current && iframeRef.current.contentWindow) {
          try {
            // Send listening command
            iframeRef.current.contentWindow.postMessage(
              JSON.stringify({ event: 'listening' }),
              'https://www.youtube.com'
            );
            // Also send command to add event listener
            iframeRef.current.contentWindow.postMessage(
              JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }),
              'https://www.youtube.com'
            );
            console.log('Sent listening command to YouTube iframe');
          } catch (e) {
            console.log('Error sending postMessage to iframe:', e);
          }
        }
      };

      // Wait for iframe to be ready, then send listening command
      const listenTimer = setTimeout(sendListeningCommand, 1000);

      return () => {
        window.removeEventListener('message', handleMessage);
        clearTimeout(listenTimer);
      };
    }

    // Cleanup on unmount
    return () => {
      // Log video duration if user leaves page while video is playing
      if (currentVideoUrlRef.current && videoStartTimeRef.current) {
        logVideoDuration(currentVideoUrlRef.current);
      }
    };
  }, [pageData, currentIndex, logVideoDuration]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
        <div className="text-2xl">載入中...</div>
      </div>
    );
  }

  if (requireLogin) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        padding: '20px'
      }}>
        <div style={{
          background: 'rgba(255, 255, 255, 0.95)',
          padding: '40px',
          borderRadius: '16px',
          boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
          maxWidth: '400px',
          width: '100%'
        }}>
          <h1 style={{
            fontSize: '28px',
            fontWeight: '700',
            marginBottom: '16px',
            textAlign: 'center',
            color: '#1e293b'
          }}>顯示器登入</h1>
          <p style={{
            color: '#64748b',
            marginBottom: '24px',
            textAlign: 'center',
            fontSize: '14px'
          }}>此顯示器需要密碼才能訪問</p>

          <form onSubmit={(e) => {
            e.preventDefault();
            handleLogin(false);
          }}>
            <div style={{ marginBottom: '16px' }}>
              <input
                type="password"
                style={{
                  width: '100%',
                  padding: '12px 16px',
                  background: '#f8fafc',
                  color: '#1e293b',
                  border: '2px solid #e2e8f0',
                  borderRadius: '8px',
                  fontSize: '15px',
                  outline: 'none',
                  boxSizing: 'border-box',
                  transition: 'border-color 0.2s'
                }}
                placeholder="請輸入密碼"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                disabled={isLoggingIn}
                autoFocus
                onFocus={(e) => e.target.style.borderColor = '#3b82f6'}
                onBlur={(e) => e.target.style.borderColor = '#e2e8f0'}
              />
            </div>

            {loginError && (
              <div style={{
                marginBottom: '16px',
                padding: '12px',
                background: '#fee2e2',
                border: '1px solid #ef4444',
                borderRadius: '8px',
                color: '#991b1b',
                fontSize: '14px'
              }}>
                {loginError}
              </div>
            )}

            <button
              type="submit"
              style={{
                width: '100%',
                padding: '14px',
                background: isLoggingIn ? '#93c5fd' : '#3b82f6',
                color: 'white',
                fontWeight: '600',
                borderRadius: '8px',
                border: 'none',
                cursor: isLoggingIn ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                transition: 'background 0.2s'
              }}
              disabled={isLoggingIn}
              onMouseEnter={(e) => !isLoggingIn && (e.target.style.background = '#2563eb')}
              onMouseLeave={(e) => !isLoggingIn && (e.target.style.background = '#3b82f6')}
            >
              {isLoggingIn ? '登入中...' : '登入'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
      }}>
        <div style={{
          fontSize: '24px',
          color: '#fee2e2',
          background: 'rgba(239, 68, 68, 0.2)',
          padding: '24px 32px',
          borderRadius: '12px',
          border: '2px solid #ef4444'
        }}>
          錯誤: {error}
        </div>
      </div>
    );
  }

  // Display Timer
  if (pageData?.type === 'timer') {
    // Log timer play (only once per timer)
    const timerId = pageData.data?._id || pageData.data?.id;
    const contentId = `timer_${timerId}`;
    if (lastContentIdRef.current !== contentId) {
      lastContentIdRef.current = contentId;
      logContentPlay('timer', {
        timerId: timerId,
        timerName: pageData.data?.name
      });
    }

    return (
      <div style={{ width: '100vw', height: '100vh', overflow: 'hidden', margin: 0, padding: 0 }}>
        <iframe
          src={pageData.data.fullLink}
          style={{ width: '100%', height: '100%', border: 'none', display: 'block' }}
          title="Timer Display"
        />
      </div>
    );
  }

  // Display Empty State
  if (pageData?.type === 'empty') {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-900 text-white">
        <div className="text-center">
          <h1 className="text-4xl mb-4">{pageData.display.名稱}</h1>
          <p className="text-2xl text-gray-400">{pageData.message}</p>
        </div>
      </div>
    );
  }

  // Display Carousel
  if (pageData?.type === 'carousel' && pageData.data.length > 0) {
    const currentItem = pageData.data[currentIndex];
    const videoId = currentItem.影片 ? getYouTubeId(currentItem.影片) : null;

    return (
      <div style={{
        width: '100vw',
        height: '100vh',
        backgroundColor: 'black',
        overflow: 'hidden',
        position: 'relative'
      }}>
        {/* Video loading indicator */}
        {videoLoading && currentItem.影片 && videoId && (
          <div style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            backgroundColor: 'black',
            zIndex: 10
          }}>
            <div style={{
              width: '60px',
              height: '60px',
              border: '4px solid rgba(255,255,255,0.3)',
              borderTop: '4px solid white',
              borderRadius: '50%',
              animation: 'spin 1s linear infinite'
            }} />
            <style>{`
              @keyframes spin {
                0% { transform: rotate(0deg); }
                100% { transform: rotate(360deg); }
              }
            `}</style>
          </div>
        )}

        {/* Content display - Video or Image (mutually exclusive) */}
        {currentItem.影片 && videoId ? (
          // Video player - using direct iframe embed
          <iframe
            ref={iframeRef}
            key={`video-iframe-${videoId}-${videoKey}`}
            src={`https://www.youtube.com/embed/${videoId}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&playsinline=1&enablejsapi=1&origin=${encodeURIComponent(window.location.origin)}`}
            style={{
              width: '100%',
              height: '100%',
              border: 'none',
              position: 'absolute',
              top: 0,
              left: 0
            }}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="YouTube Video"
            onLoad={() => {
              console.log('YouTube iframe loaded');
              setVideoLoading(false);
              // Send listening command after iframe loads
              setTimeout(() => {
                if (iframeRef.current && iframeRef.current.contentWindow) {
                  try {
                    iframeRef.current.contentWindow.postMessage(
                      JSON.stringify({ event: 'listening' }),
                      '*'
                    );
                    iframeRef.current.contentWindow.postMessage(
                      JSON.stringify({ event: 'command', func: 'addEventListener', args: ['onStateChange'] }),
                      '*'
                    );
                    console.log('Sent listening commands to YouTube iframe');
                  } catch (e) {
                    console.log('Error sending postMessage:', e);
                  }
                }
              }, 500);
            }}
            onError={() => {
              console.error('YouTube iframe error');
              setVideoLoading(false);
              // Skip to next slide on error
              setTimeout(() => {
                setCurrentIndex((prev) => (prev + 1) % pageData.data.length);
              }, 2000);
            }}
          />
        ) : currentItem.圖片 ? (
          // Image display
          <img
            key={`image-${currentIndex}-${videoKey}`}
            src={currentItem.圖片}
            alt="Carousel"
            style={{ width: '100%', height: '100%', objectFit: 'contain' }}
            onError={(e) => {
              console.error('Image load error:', currentItem.圖片);
              // On image error, skip to next slide after a delay
              setTimeout(() => {
                setCurrentIndex((prev) => (prev + 1) % pageData.data.length);
              }, 2000);
            }}
          />
        ) : (
          // Fallback for invalid content
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', width: '100%', height: '100%', color: 'white', fontSize: '2rem' }}>無效的輪播內容</div>
        )}
      </div>
    );
  }

  return null;
};

export default DisplayPage;
